import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";
import CircularProgress from "@mui/material/CircularProgress";
import { Alert } from "@mui/material"; // Import Alert component
import useCurrentUser from "../components/user/useCurrentUser";
import ProfileHeader from "../components/profile/profileHeader";
import ProfileMainSection from "../components/profile/profileMainSection.jsx"; // Update import statement
import ProfileTaskSection from "../components/profile/profileTaskSection";
import ProfileFooter from "../components/profile/profileFooter";

import {
  getUserTasks,
  getUserTasks_history,
  getUserTasks_currentWeek,
  getUserTasks_currentMonth,
} from "../api/apiProfile"; // Import all functions
import supabase from "../api/supabase"; // Import Supabase client
import "./Profile.scss";

const UserProfile = () => {
  const { currentUser, loading: userLoading } = useCurrentUser();
  const queryClient = useQueryClient(); // Initialize Query Client

  // Fetch current user tasks using React Query
  const { data: userTaskManager, isLoading: taskLoading } = useQuery({
    queryKey: ["userTasks", currentUser?.empUserId], // Query key as an array
    queryFn: () => getUserTasks(currentUser.empUserId), // Fetch function
    enabled: !!currentUser, // Enable query only when currentUser is available
    refetchOnWindowFocus: false, // Optionally disable refetch on window focus
  });

  // Fetch historical user tasks using React Query
  const { data: userTaskManagerHistory, isLoading: historyLoading } = useQuery({
    queryKey: ["userTasksHistory", currentUser?.empUserId], // Query key as an array
    queryFn: () => getUserTasks_history(currentUser.empUserId), // Fetch function
    enabled: !!currentUser, // Enable query only when currentUser is available
    refetchOnWindowFocus: false, // Optionally disable refetch on window focus
  });

  // Fetch user tasks for the current week using React Query
  const { data: userTasksCurrentWeek, isLoading: weekLoading } = useQuery({
    queryKey: ["userTasksCurrentWeek", currentUser?.empUserId], // Query key for current week
    queryFn: () => getUserTasks_currentWeek(currentUser.empUserId), // Fetch function
    enabled: !!currentUser, // Enable query only when currentUser is available
    refetchOnWindowFocus: false, // Optionally disable refetch on window focus
  });

  // Fetch user tasks for the current month using React Query
  const { data: userTasksCurrentMonth, isLoading: monthLoading } = useQuery({
    queryKey: ["userTasksCurrentMonth", currentUser?.empUserId], // Query key for current month
    queryFn: () => getUserTasks_currentMonth(currentUser.empUserId), // Fetch function
    enabled: !!currentUser, // Enable query only when currentUser is available
    refetchOnWindowFocus: false, // Optionally disable refetch on window focus
  });

  // Subscribe to real-time updates from Supabase for both current tasks and historical tasks
  useEffect(() => {
    if (currentUser) {
      // Subscribe to taskManager real-time updates
      const taskChannel = supabase
        .channel("taskManager:realtime")
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "taskManager" },
          () => {
            // Invalidate queries for userTasks and real-time updates for the week and month
            queryClient.invalidateQueries({
              queryKey: ["userTasks", currentUser.empUserId],
            });
          }
        )
        .subscribe();

      // Subscribe to taskManager_history real-time updates
      const taskHistoryChannel = supabase
        .channel("taskManager_history:realtime")
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "taskManager_history" },
          () => {
            queryClient.invalidateQueries({
              queryKey: ["userTasksHistory", currentUser.empUserId],
            });
            queryClient.invalidateQueries({
              queryKey: ["userTasksCurrentWeek", currentUser.empUserId],
            });
            queryClient.invalidateQueries({
              queryKey: ["userTasksCurrentMonth", currentUser.empUserId],
            });
          }
        )
        .subscribe();

      return () => {
        // Cleanup on component unmount
        supabase.removeChannel(taskChannel);
        supabase.removeChannel(taskHistoryChannel);
      };
    }
  }, [currentUser, queryClient]);

  // Show a loading message while fetching user data
  if (
    userLoading ||
    taskLoading ||
    historyLoading ||
    weekLoading ||
    monthLoading
  ) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress sx={{ color: "#ad6544" }} />
      </div>
    );
  }

  // Inside your component where you check for no user data:
  if (!currentUser) {
    return (
      <Alert severity="info" sx={{ margin: "20px", textAlign: "center" }}>
        Please sign in for access.
      </Alert>
    );
  }

  // Display the user's data
  return (
    <div className="profile-container max-w-md mx-auto p-0 flex flex-col justify-center">
      <ProfileHeader currentUser={currentUser} />
      <ProfileMainSection
        userId={currentUser.empUserId}
        userTaskManager={userTaskManager} // Pass current user tasks
        userTaskManager_history={userTaskManagerHistory} // Pass historical user tasks
        userTasksCurrentWeek={userTasksCurrentWeek} // Pass tasks from current week
        userTasksCurrentMonth={userTasksCurrentMonth} // Pass tasks from current month
      />
      <ProfileTaskSection
        userTaskManager={userTaskManager} // Pass current user tasks
        userTaskManager_history={userTaskManagerHistory} // Pass historical user tasks
        userTasksCurrentWeek={userTasksCurrentWeek} // Pass tasks from current week
        userTasksCurrentMonth={userTasksCurrentMonth} // Pass tasks from current month
      />

      <ProfileFooter empUserId={currentUser.empUserId} />
    </div>
  );
};
// Add prop validation using PropTypes
ProfileFooter.propTypes = {
  empUserId: PropTypes.string.isRequired, // empUserId is required and must be a string
};

export default UserProfile;
